﻿import { formatUnixTimestamp } from '../../utils';
import { PhotoThumbnail } from './PhotoThumbnail';
import { normalizeSchoolName } from './school-name';

/**
 * Column Render Utilities
 *
 * Reusable cell renderers for common data types across all report pages.
 * These utilities eliminate 400+ lines of duplicated render logic.
 *
 * Usage: Import and use directly in column definitions
 * Example: { key: 'isEnabled', label: 'Activat', render: renderBoolean }
 */

/**
 * Boolean cell renderer
 * Displays: DA (true) / NU (false) / - (undefined/null)
 *
 * @example
 * { key: 'isValidated', label: 'Validat', render: renderBoolean }
 */
export const renderBoolean = (value: boolean | undefined | null): string => {
    if (value === true) return 'DA';
    if (value === false) return 'NU';
    return '-';
};

/**
 * Nested field cell renderer factory
 * Safely accesses nested object properties with fallback
 *
 * @param getValue - Function to extract nested value from row
 * @returns Render function for DataTable column
 *
 * @example
 * {
 *   key: 'school.address.city',
 *   label: 'Oraș',
 *   render: renderNestedField(row => row.school?.address?.city)
 * }
 */
export const renderNestedField = <T,>(
    getValue: (row: T) => any
) => (_: any, row: T): string => {
    const value = getValue(row);
    return value != null ? String(value) : '-';
};

/**
 * Timestamp cell renderer
 * Converts Unix timestamp to formatted date string (Romanian locale)
 * Format: "1 ianuarie 2025, 14:30"
 *
 * @example
 * { key: 'timestamp', label: 'Data înregistrare', render: renderTimestamp }
 */
export const renderTimestamp = (value: Date | number | string | undefined | null): string => {
    if (!value) return '-';
    const formatted = formatUnixTimestamp(value);
    return formatted || '-';
};

/**
 * Link cell renderer factory
 * Creates clickable links that open in new window
 *
 * @param label - Link text to display
 * @param className - Optional CSS classes
 * @returns Render function that generates <a> element
 *
 * @example
 * { key: 'documentUrl', label: 'Document', render: renderLink('Vezi Document', 'text-magenta') }
 */
export const renderLink = (label: string, className?: string) =>
    (value: string | undefined | null): JSX.Element | string => {
        if (!value) return '-';

        return (
            <a
                href={value}
                target="_blank"
                rel="noopener noreferrer"
                className={className || "text-magenta hover:text-magenta-hover underline text-smaller"}
            >
                {label}
            </a>
        );
    };

/**
 * Photo cell renderer
 * Specialized link renderer for photo URLs
 * Displays "Vezi Foto" in blue
 *
 * @example
 * { key: 'photo1', label: 'Foto 1', render: renderPhoto }
 */
export const renderPhoto = renderLink('Vezi Foto', 'text-magenta hover:text-magenta-hover underline text-smaller');

/**
 * Document cell renderer
 * Specialized link renderer for document URLs
 * Displays "Vezi Document" in green
 *
 * @example
 * { key: 'deeeOrderDocument', label: 'Document DEEE', render: renderDocument }
 */
export const renderDocument = renderLink('Vezi Document', 'text-magenta hover:text-magenta-hover underline text-smaller');

/**
 * Wrapped text cell renderer factory
 * For long text fields that need wrapping with max-width
 *
 * @param maxWidth - Maximum width CSS value (default: '350px')
 * @returns Render function that wraps text in div
 *
 * @example
 * { key: 'description', label: 'Descriere', render: renderWrappedText('400px') }
 */
export const renderWrappedText = (maxWidth: string = '350px') =>
    (value: string | undefined | null): JSX.Element => (
        <div className="text-wrap" style={{ maxWidth }}>
            {value || '-'}
        </div>
    );

/**
 * Plain text renderer with fallback
 * For fields that need simple string rendering with null safety
 *
 * @example
 * { key: 'status', label: 'Status', render: renderText }
 */
export const renderText = (value: any): string => {
    return value != null ? String(value) : '-';
};

/**
 * Photo thumbnail renderer with modal preview
 * Displays 64x64px thumbnail that opens fullscreen in modal dialog
 * Uses existing photo-upload utilities from design system
 *
 * @example
 * {
 *   key: 'photos[0]',
 *   label: 'Foto 1',
 *   render: renderPhotoThumbnail,
 *   accessor: (row) => row.photos?.[0]
 * }
 */
export const renderPhotoThumbnail = (value: string | undefined | null): JSX.Element => {
    return <PhotoThumbnail url={value} />;
};

/**
 * Nume de scoala, normalizat DOAR la afisare
 *
 * Majoritatea numelor vin din importul MEC, integral cu majuscule. Intr-un tabel
 * de 500 de randuri, un bloc de majuscule se citeste greu. Vezi
 * [`school-name.ts`](./school-name.ts) pentru regula exacta si pentru ce NU
 * repara (ghilimele rupte, localitate lipita de nume).
 *
 * ⚠️ Datele, exportul Excel si filtrul pe coloana raman pe textul original.
 * Filtrul e case-insensitive in backend, deci cautarea dupa ce se vede
 * functioneaza in continuare.
 *
 * @example
 * columnBuilder<SchoolDto>().text('numeInstitutieInvatamant', 'Nume', 'Denumire...', {
 *     render: renderSchoolName,
 *     cardRole: 'title',
 * })
 */
export const renderSchoolName = (value: string | undefined | null): string => {
    const normalizat = normalizeSchoolName(value);
    return normalizat || '-';
};
